import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Subscribe } from 'unstated';
import { defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import type { ClassificationOption } from '@atlassian/data-classification-level';
import { EditPopup, ReadPopup } from '@atlassian/data-classification-level';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { fg } from '@confluence/feature-gating';

import { FlagStatus } from '../constants/FlagStatus.ts';
import { SaveFlag } from '../SaveFlag.tsx';
import type { ContentClassificationMetadata } from '../hooks/useContentClassification.ts';
import { getAllClassificationLevels } from '../utils/getAllClassificationLevels.ts';
import { useDefaultAndSpaceClassificationLevels } from '../hooks/useDefaultAndSpaceClassificationLevels.ts';
import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';
import { useEditSubmitCallback } from '../hooks/useEditSubmitCallback.ts';
import { useComputeDefaultClassificationDropdownOption } from '../hooks/useComputeDefaultClassificationDropdownOption.ts';

import { ContentClassificationTag } from './ContentClassificationTag.tsx';

type EditorContentClassificationTagProps = {
	contentId: string;
	contentType: string;
	contentClassificationMetadata: ContentClassificationMetadata;
	isEditMode: boolean;
	spaceKey: string;
	isInEditorTitle?: boolean;
	isTitleCenterAligned?: boolean;
	userOffline?: boolean;
};

type TagWrapperProps = {
	isInEditorTitle: boolean;
	isTitleCenterAligned: boolean;
	shouldChangeTagPlacement: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const TagWrapper = styled.div((props: TagWrapperProps) => ({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
	justifyContent:
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		props.isInEditorTitle && props.isTitleCenterAligned ? 'center' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	position: props.shouldChangeTagPlacement ? 'absolute' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	top: props.shouldChangeTagPlacement ? '-33px' : 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	left: props.shouldChangeTagPlacement ? '5px' : 'unset',
}));

const messages = defineMessages({
	errorTitle: {
		id: 'data-classification.content-classification.error-flag.title',
		defaultMessage: 'Update to classification failed to save',
		description: "Title for error flag shown when page's classification failed to save",
	},
	errorDescription: {
		id: 'data-classification.content-classification.error-flag.description',
		defaultMessage: 'Please try again.',
		description: "Secondary message for error flag shown when page's classification failed to save",
	},
});

type LazierEditPopupProps = {
	spaceKey: string;
	selectedOption?: ClassificationOption | null;
	onEditCancel: () => void;
	onEditSubmit: (classification: ClassificationOption) => void;
	isDisabled: boolean;
	loading: boolean;
};

const LazierEditPopup = (props: LazierEditPopupProps) => {
	const {
		selectedOption: selectedOptionProp,
		onEditCancel,
		onEditSubmit,
		isDisabled: isDisabledProp,
		loading: loadingProp,
		spaceKey,
	} = props;

	const {
		defaultClassification: spaceDefaultClassification,
		classificationLevels,
		getClassificationLevelFromId,
		expectedError,
		loading: spaceAndDefaultLoading,
	} = useDefaultAndSpaceClassificationLevels({
		spaceKey,
		shouldPreloadData: true,
	});

	const {
		defaultClassificationOption,
		error: defaultClassificationError,
		loading: orgDefaultLoading,
	} = useComputeDefaultClassificationDropdownOption({
		spaceDefaultClassificationLevel: spaceDefaultClassification,
		getClassificationLevelFromId,
	});

	const allClassificationLevels: ClassificationLevel[] = [
		...getAllClassificationLevels(classificationLevels),
		defaultClassificationOption,
	];
	const selectedOption = selectedOptionProp || defaultClassificationOption;

	const hasError = !!expectedError || !!defaultClassificationError;

	const combinedLoadingCondition = loadingProp || spaceAndDefaultLoading || orgDefaultLoading;
	if (combinedLoadingCondition) {
		return null;
	}

	return (
		<EditPopup
			options={allClassificationLevels}
			selectedOption={selectedOption || undefined}
			onEditCancel={onEditCancel}
			onEditSubmit={onEditSubmit}
			errorMessage={hasError ? 'Unable to load classification. Try again.' : undefined}
			loading={combinedLoadingCondition}
			isDisabled={isDisabledProp || combinedLoadingCondition}
		/>
	);
};

// Responsible for all editor-specific classification functionality (i.e. function for rendering Select Classification popup component & rendering error flag)
const EditorContentClassificationTagComponent = ({
	contentId,
	contentType,
	contentClassificationMetadata,
	isEditMode,
	spaceKey,
	isInEditorTitle = false,
	isTitleCenterAligned = false,
	userOffline = false,
}: EditorContentClassificationTagProps) => {
	const {
		classification: currentClassification,
		flagStatus,
		shouldDisplayClassification,
		spaceDefaultIsOverridden,
	} = contentClassificationMetadata;

	const selectedOption = useMemo(() => {
		if (!spaceDefaultIsOverridden) {
			return undefined;
		} else {
			return currentClassification;
		}
	}, [currentClassification, spaceDefaultIsOverridden]);

	const handleEditSubmit = useEditSubmitCallback({
		contentId,
		contentClassificationMetadata,
	});
	const onEditSubmit = (classification: ClassificationOption, closePopup: () => void) => {
		handleEditSubmit(classification);
		closePopup();
	};

	if (!shouldDisplayClassification || !currentClassification) {
		return null;
	}

	const renderPopupComponent = (closePopup: () => void) => {
		if (isEditMode) {
			return (
				<LazierEditPopup
					spaceKey={spaceKey}
					selectedOption={selectedOption}
					onEditCancel={closePopup}
					onEditSubmit={(classification: ClassificationOption) => {
						onEditSubmit(classification, closePopup);
					}}
					loading={flagStatus === FlagStatus.LOADING}
					isDisabled={userOffline}
				/>
			);
		}

		// For read mode, determine guideline representation based on feature flag and data availability
		const guidelineRepresentation =
			currentClassification.guidelineADF && fg('confluence_dlp_guideline_rich_text_support')
				? 'EDITOR'
				: 'PLAIN';

		return (
			<ReadPopup
				name={currentClassification.name}
				guideline={currentClassification.guideline}
				guidelineADF={currentClassification.guidelineADF}
				guidelineRepresentation={guidelineRepresentation}
			/>
		);
	};

	return (
		<TagWrapper
			isInEditorTitle={isInEditorTitle}
			isTitleCenterAligned={isTitleCenterAligned}
			shouldChangeTagPlacement={false}
		>
			<ContentClassificationTag
				contentId={contentId}
				contentType={contentType || null}
				contentClassificationMetadata={contentClassificationMetadata}
				renderPopupComponent={userOffline ? undefined : renderPopupComponent}
				isEditMode
			/>
			{flagStatus === FlagStatus.LOADING && <Spinner size="small" />}
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) =>
					flagStatus === FlagStatus.ERROR && (
						<SaveFlag
							flags={flags}
							title={messages.errorTitle}
							description={messages.errorDescription}
							type="warning"
							showRefresh={false}
						/>
					)
				}
			</Subscribe>
		</TagWrapper>
	);
};

const EditorContentClassificationTagGetter: FC<EditorContentClassificationTagProps> = (props) => {
	return <EditorContentClassificationTagComponent {...props} />;
};

const EditorContentClassificationTagWithErrorBoundary = withTransparentErrorBoundary({
	attribution: Attribution.DLP,
})(EditorContentClassificationTagGetter);

export const EditorContentClassificationTag: FC<EditorContentClassificationTagProps> = (props) => (
	<EditorContentClassificationTagWithErrorBoundary {...props} />
);
